import React, { useEffect, useRef, useState } from 'react';
import {
  createSellerProperty,
  editSellerProperty,
  getSellerProperty,
  publishProperty,
} from '../../api';
import { handleGlobalApiCalls } from '../../helpers/asyncActions';
import { getFormattedDate } from '../../helpers/time';
import useOptionsData from '../../hooks/useOptionsData';
import { useGlobalContext } from '../../stores/global';
import Form1 from '../propertyForms/Form1';
import Form2 from '../propertyForms/Form2';
import Form3 from '../propertyForms/Form3';
import Form31 from '../propertyForms/Form31';
import Form31b from '../propertyForms/Form31b';
import Form32 from '../propertyForms/Form32';
import Form33 from '../propertyForms/Form33';
import Form33b from '../propertyForms/Form33b';
import Form4 from '../propertyForms/Form4';
import Form5 from '../propertyForms/Form5';
import Form6 from '../propertyForms/Form6';
import Form7 from '../propertyForms/Form7';
import Form8 from '../propertyForms/Form8';
import FormAppointment from '../propertyForms/FormAppointment';
import FormSeparation from '../propertyForms/FormSeparation';
import StepForm from '../StepForm/StepForm';
import StepFormProgress from '../StepForm/StepFormProgress';

const stepsDescriptions = [
  {
    title: 'Identifica tu propiedad',
    desc: 'Tipo de propiedad, descripción y más',
  },
  {
    title: 'Presenta tu propiedad',
    desc: 'Fotos y videos',
  },
  {
    title: 'Diseña tu oferta',
    desc: 'Tipo de venta, matrícula y más',
  },
];

function parsePropertyDataFromApi(dataFromApi: any = {}) {
  return {
    ...dataFromApi,
    step: dataFromApi.step,
    description: dataFromApi.description,
    features: dataFromApi.features,
    deliveryDate: getFormattedDate(dataFromApi.deliveryDate),

    location: dataFromApi.location,
    addressName: dataFromApi.address?.name,
    addressCountry: dataFromApi.address?.country,
    addressState: dataFromApi.address?.state,
    addressCity: dataFromApi.address?.city,
    addressNeighbor: dataFromApi.address?.neighbor,

    infoType: dataFromApi.info?.type,
    infoEstrato: dataFromApi.info?.estrato,
    infoSquareMeters: dataFromApi.info?.squareMeters,
    infoRooms: dataFromApi.info?.rooms,
    infoToilets: dataFromApi.info?.toilets,
    infoConstructionYear: dataFromApi.info?.constructionYear,
    infoParkings: dataFromApi.info?.parkings,

    pricesProperty: dataFromApi.prices?.property,
    pricesAdministration: dataFromApi.prices?.administration,
    pricesTax: dataFromApi.prices?.tax,
    pricesMonthlyInterest: dataFromApi.prices?.monthlyInterest,
    pricesPenaltyFee: dataFromApi.prices?.penaltyFee,
    pricesSeparationPayment: dataFromApi.prices?.separationPayment,
    pricesCreditQuote: dataFromApi.prices?.creditQuote,

    salesType: dataFromApi.salesType,

    registrationNumber: dataFromApi.registrationNumber,
    registrationValue: dataFromApi.registrationValue,

    ownerName: dataFromApi.owner?.firstName,
    ownerLastName: dataFromApi.owner?.lastName,
    ownerDocumentType: dataFromApi.owner?.document?.type,
    ownerDocumentNumber: dataFromApi.owner?.document?.number,

    attaches: dataFromApi.attaches,

    availability: dataFromApi.availability,
    meetingInterval: dataFromApi.meetingInterval,

    reservedDays: dataFromApi.reservedDays,
    reservedPrice: dataFromApi.reservedPrice,
  };
}

function parseStepDataForApi(formData: Record<string, any>) {
  return {
    step: formData.step,
    description: formData.description,
    deliveryDate:
      formData.salesType === 'sobre plano'
        ? getFormattedDate(formData.deliveryDate)
        : undefined,
    features: formData.features,
    attaches: formData.attaches,

    location: formData.location,
    address: {
      name: formData.addressName,
      country: formData.addressCountry,
      state: formData.addressState,
      city: formData.addressCity,
      neighbor: formData.addressNeighbor,
    },
    prices: {
      property: formData.pricesProperty || undefined,
      administration: formData.pricesAdministration || undefined,
      tax: formData.pricesTax || undefined,
      separationPayment: formData.pricesSeparationPayment || undefined,
      penaltyFee: formData.pricesPenaltyFee || undefined,
      monthlyInterest: formData.pricesMonthlyInterest || undefined,
      creditQuote: formData.pricesCreditQuote || undefined,
    },
    info: {
      type: formData.infoType,
      estrato: formData.infoEstrato,
      squareMeters: formData.infoSquareMeters,
      rooms: formData.infoRooms,
      toilets: formData.infoToilets,
      constructionYear:
        formData.salesType === 'construido'
          ? formData.infoConstructionYear
          : undefined,
      parkings: formData.infoParkings,
    },
    owner: formData.ownerName
      ? {
          firstName: formData.ownerName,
          lastName: formData.ownerLastName,
          document: {
            type: formData.ownerDocumentType,
            number: formData.ownerDocumentNumber,
          },
        }
      : undefined,
    salesType: formData.salesType,
    registrationNumber:
      formData.salesType === 'construido' ? formData.registrationNumber : '',

    availability: formData.availability,
    meetingInterval: formData.meetingInterval,

    reservedDays: formData.reservedDays,
    reservedPrice: formData.reservedPrice,
  };
}

interface Props {
  propertyId?: string;
  onClose?: () => void;
}

export default function SellerPropertyForm(props: Props) {
  const { isLoading, dispatch } = useGlobalContext();
  const [localLoading, setLocalLoading] = useState(true);
  const [propertyId, setPropertyId] = useState<string | undefined>(
    props.propertyId
  );
  const [activeStep, setActiveStep] = useState(0);
  const formData = useRef(parsePropertyDataFromApi());
  const [features, setFeatures] = useState<{ name: string }[]>([]);
  const [salesTypes, setSalesTypes] = useState<string[]>([]);
  const [documentTypes, setDocumentTypes] = useState<string[]>([]);
  const [propertyTypes, setPropertyTypes] = useState<string[]>();
  const { data: optionsData } = useOptionsData();

  useEffect(() => {
    if (!optionsData) return;

    setFeatures(optionsData.features);
    setSalesTypes(optionsData.salesType);
    setPropertyTypes(optionsData.propertyType);
    setDocumentTypes(optionsData.document);

    handleGlobalApiCalls(dispatch, isLoading, null, () =>
      Promise.resolve(propertyId ? getSellerProperty(propertyId) : null)
        .then((response) => {
          const formData = parsePropertyDataFromApi(response?.data?.data);
          setActiveStep(formData.step ?? 0);
          return formData;
        })
        .then((data) => (formData.current = data))
    ).finally(() => setLocalLoading(false));
  }, [optionsData]);

  const close = () => {
    props.onClose?.();
  };

  let stepsMap: {
    initialSubstep?: true;
    saveMainStep?: true;
    forceSave?: boolean;
    progress?: number;
    title?: [string, string];
    stepEl: React.ReactNode;
  }[] = [];

  const submitFormStep = async () => {
    return handleGlobalApiCalls(dispatch, isLoading, null, () => {
      const stepDataForApi = parseStepDataForApi(formData.current);

      if (propertyId) {
        return editSellerProperty(propertyId, stepDataForApi);
      }

      return createSellerProperty(stepDataForApi).then((response) => {
        setPropertyId(response.data.data?.id);
      });
    });
  };

  const handleBackClick = () => {
    const newStep = activeStep - 1;

    if (newStep < 0) {
      close();
      return;
    }

    setActiveStep(newStep);
  };

  const handleNextClick = async (data?: { [x: string]: any }) => {
    const newStep = activeStep + 1;
    const { saveMainStep, forceSave } = stepsMap[newStep];

    formData.current = { ...formData.current, ...data, step: newStep };

    try {
      if ((saveMainStep && propertyId) || forceSave) {
        await submitFormStep();
      }
    } catch (e) {
      return;
    }

    setActiveStep(newStep);
  };

  const handleProgressStepClick = (mainStep: number) => {
    let newStep = 0;

    for (let appearances = 0, idx = 0; idx < stepsMap.length; idx++) {
      if (stepsMap[idx].initialSubstep && appearances === mainStep) {
        newStep = idx;
        break;
      } else if (stepsMap[idx].initialSubstep) appearances++;
    }

    setActiveStep(newStep);
  };

  const handlePublishClick = () => {
    if (isLoading || !propertyId) {
      return;
    }

    handleGlobalApiCalls(dispatch, isLoading, null, () =>
      publishProperty(propertyId)
        .finally(() => dispatch({ type: 'SET_LOADING', payload: false }))
        .then(() => {
          dispatch({
            type: 'SET_ALERT_MODAL',
            payload: {
              title: 'Propiedad publicada!',
              message: 'Tu propiedad ha sido publicada!',
            },
          });
        })
        .then(close)
        .catch((err) => {
          if (!err?.response?.data?.errors?.includes?.('E:images.not_enough'))
            throw err;

          dispatch({
            type: 'SET_ALERT_MODAL',
            payload: {
              title: 'Error',
              message:
                'Número de imágenes insuficiente para publicar tu propiedad',
            },
          });
        })
    );
  };

  const handleExitClick = async () => {
    close();
  };

  const getActiveStepInfo = (activeStep: number) => {
    if (localLoading) return { stepEl: null };

    stepsMap = [
      {
        initialSubstep: true,
        title: ['Paso 1', stepsDescriptions[0].title],
        stepEl: (
          <Form1
            types={propertyTypes}
            defaultValues={formData.current}
            onBackClick={handleBackClick}
            onSubmit={handleNextClick}
          />
        ),
      },
      {
        title: ['Paso 1', stepsDescriptions[0].title],
        stepEl: (
          <Form2
            defaultValues={formData.current}
            onBackClick={handleBackClick}
            onSubmit={handleNextClick}
          />
        ),
      },
      {
        title: ['Paso 1', stepsDescriptions[0].title],
        stepEl: (
          <Form3
            defaultValues={formData.current}
            onBackClick={handleBackClick}
            onSubmit={handleNextClick}
          />
        ),
      },
      {
        title: ['Paso 1', stepsDescriptions[0].title],
        stepEl: (
          <Form4
            features={features}
            defaultValues={formData.current}
            onBackClick={handleBackClick}
            onSubmit={handleNextClick}
          />
        ),
      },
      {
        title: ['Paso 1', stepsDescriptions[0].title],
        stepEl: (
          <Form5
            defaultValues={formData.current}
            onBackClick={handleBackClick}
            onSubmit={handleNextClick}
          />
        ),
      },
      {
        saveMainStep: true,
        stepEl: (
          <StepFormProgress
            property={formData.current}
            onBackClick={handleBackClick}
            onNextClick={handleNextClick}
            onStepClick={handleProgressStepClick}
            activeMainStep={1}
            stepsDescriptions={stepsDescriptions}
          />
        ),
      },
      {
        initialSubstep: true,
        title: ['Paso 2', stepsDescriptions[1].title],
        stepEl: (
          <Form6
            defaultValues={formData.current}
            onBackClick={handleBackClick}
            onSubmit={handleNextClick}
          />
        ),
      },
      {
        title: ['Paso 2', stepsDescriptions[1].title],
        stepEl: (
          <Form7
            defaultValues={formData.current}
            onBackClick={handleBackClick}
            onSubmit={handleNextClick}
          />
        ),
      },
      {
        saveMainStep: true,
        stepEl: (
          <StepFormProgress
            property={formData.current}
            onBackClick={handleBackClick}
            onNextClick={handleNextClick}
            onStepClick={handleProgressStepClick}
            activeMainStep={2}
            stepsDescriptions={stepsDescriptions}
          />
        ),
      },
      {
        initialSubstep: true,
        title: ['Paso 3', stepsDescriptions[2].title],
        stepEl: (
          <Form8
            salesTypes={salesTypes}
            defaultValues={formData.current}
            onBackClick={handleBackClick}
            onSubmit={handleNextClick}
          />
        ),
      },
      {
        title: ['Paso 3', stepsDescriptions[2].title],
        stepEl:
          formData.current.salesType === 'construido' ? (
            <Form31
              defaultValues={formData.current}
              onBackClick={handleBackClick}
              onSubmit={handleNextClick}
            />
          ) : (
            <Form31b
              defaultValues={formData.current}
              onBackClick={handleBackClick}
              onSubmit={handleNextClick}
            />
          ),
      },
      {
        title: ['Paso 3', stepsDescriptions[2].title],
        forceSave: formData.current.salesType === 'construido',
        stepEl: (
          <Form32
            types={documentTypes}
            salesType={formData.current.salesType}
            defaultValues={formData.current}
            onBackClick={handleBackClick}
            onSubmit={handleNextClick}
          />
        ),
      },
      {
        title: ['Paso 3', stepsDescriptions[2].title],
        stepEl:
          formData.current.salesType === 'construido' ? (
            <Form33
              defaultValues={formData.current}
              onBackClick={handleBackClick}
              onSubmit={handleNextClick}
            />
          ) : (
            <Form33b
              defaultValues={formData.current}
              onBackClick={handleBackClick}
              onSubmit={handleNextClick}
            />
          ),
      },
      {
        title: ['Paso 3', stepsDescriptions[2].title],
        stepEl: (
          <FormAppointment
            defaultValues={formData.current}
            onBackClick={handleBackClick}
            onSubmit={handleNextClick}
          />
        ),
      },
      {
        title: ['Paso 3', stepsDescriptions[2].title],
        stepEl: (
          <FormSeparation
            defaultValues={formData.current}
            onBackClick={handleBackClick}
            onSubmit={handleNextClick}
          />
        ),
      },
      {
        saveMainStep: true,
        stepEl: (
          <StepFormProgress
            property={formData.current}
            onStepClick={handleProgressStepClick}
            activeMainStep={3}
            stepsDescriptions={stepsDescriptions}
            onPublishClick={handlePublishClick}
            onSaveAndExitClick={async () => {
              await submitFormStep();
              close();
            }}
          />
        ),
      },
    ];

    return stepsMap[activeStep];
  };

  const { progress, title, stepEl } = getActiveStepInfo(activeStep);

  return (
    <StepForm
      title={propertyId ? 'EDITAR PROPIEDAD' : 'AGREGAR PROPIEDAD'}
      onExitClick={handleExitClick}
      isLoading={localLoading}
      progress={progress}
      headerTitle={title}
    >
      {stepEl}
    </StepForm>
  );
}
